import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { TaskViewDialog } from '../components/features/TaskViewDialog'
import { useAuth } from '../context/AuthContext'
import { isSalesLikeRole } from '../lib/config/role-config'
import { queries } from '../lib/supabase/queries'

export const TaskDetailPage: React.FC = () => {
  const { uniqueTaskId = '' } = useParams<{ uniqueTaskId: string }>()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const taskQuery = useQuery({
    queryKey: ['task', 'unique-id', uniqueTaskId],
    queryFn: () => queries.getTaskByUniqueId(uniqueTaskId),
    enabled: Boolean(uniqueTaskId),
  })
  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  if (taskQuery.isPending || profilesQuery.isPending) {
    return <LoadingSpinner size="lg" text="Loading task..." />
  }

  if (taskQuery.isError || profilesQuery.isError) {
    return <div className="p-8 text-center text-red-700">Unable to load this task.</div>
  }

  if (!taskQuery.data) {
    return <div className="p-8 text-center text-gray-700">Task not found.</div>
  }

  const task = taskQuery.data
  const profiles = profilesQuery.data || []
  const assigneeLabel = profiles.find((candidate) => candidate.id === task.assigned_to)?.full_name || undefined
  const createdByLabel = profiles.find((candidate) => candidate.id === task.created_by)?.full_name || undefined

  return (
    <TaskViewDialog
      task={task}
      assigneeLabel={assigneeLabel}
      createdByLabel={createdByLabel}
      onClose={() => navigate('/tasks')}
      onAddSubTask={profile && !isSalesLikeRole(profile.role)
        ? () => navigate('/tasks', { state: { openSubTaskForTaskId: task.id } })
        : undefined}
      fullScreen
      headerSubtitle={profile ? 'Shareable task details.' : undefined}
    />
  )
}